import { useAuthStore } from '../stores/authStore'

const API_BASE = import.meta.env.VITE_API_URL || ''

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().token
  
  // Ensure endpoint starts with /api
  const fullEndpoint = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`
  
  const res = await fetch(`${API_BASE}${fullEndpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    }
  })
  
  if (!res.ok) {
    if (res.status === 401) {
      useAuthStore.getState().logout()
    }
    const error = await res.json().catch(() => ({ message: 'Request failed' }))
    throw new Error(error.message || error.error || 'Request failed')
  }
  
  const json = await res.json() as { success: boolean; data?: T; error?: string }
  
  if (!json.success) {
    throw new Error(json.error || 'Request failed')
  }
  
  return json.data as T
}

export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, data: unknown) => 
    request<T>(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  put: <T>(endpoint: string, data: unknown) => 
    request<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  delete: <T>(endpoint: string) => 
    request<T>(endpoint, { method: 'DELETE' })
}

// API Types
export interface Company {
  id: string
  name: string
  registration_number: string | null
  stripe_connect_status: string
  billing_email: string
  subscription_tier: string
  subscription_status: string
  employee_count: number
  active_card_count: number
  total_funded: number
  default_monthly_allowance: number
  created_at: string
}

export interface Employee {
  id: string
  company_id: string
  email: string
  name: string
  department: string | null
  status: string
  kyc_level: string
  monthly_allowance: number | null
  current_balance: number
  created_at: string
}

export interface FundingRecord {
  id: string
  company_id: string
  amount: number
  status: string
  funded_by: string
  created_at: string
}

export interface DashboardStats {
  total_companies: number
  total_employees: number
  active_cards: number
  total_funded: number
  monthly_volume: number
  pending_kyc: number
}
